import React, { useEffect, useState } from "react";
import { Box, Button, Checkbox, Dialog, FormControlLabel, Grid, MenuItem, Typography } from "@mui/material";
import { toast } from "react-toastify";

import { TCInput, TCSelect } from "components/TopCourtComponents/Input/TopCourtInput";
import ButtonComponent from "views/components/buttonComponent";
import BoxButtonComponent from "views/components/boxButtonComponent";
import ConfirmDialogComponent from "views/components/cofirmDialogComponent";

import { updateUserServiceRole } from "services/userServices";
import { TCLabel } from "components/TopCourtComponents/Label/TopCourtLabel";

export default function SetServiceRoleDialog({ merchantId, placeId, setDialog, handleClose }) {
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const [form, setForm] = useState({
        _id: "",
        phoneNumber: "",
        name: "",
        username: "",
        staff: false,
        counter: false,
        serviceActive: false,
        merchantId: null,
    });

    const [joi, setJOI] = useState({
        serviceRole: null
    });

    useEffect(() => {
        setJOI({
            serviceRole: !(form.staff || form.counter) ? " required" : "",
        });
    }, [form]);

    useEffect(() => {
        if (setDialog.data) {
            const u = setDialog.data;
            setForm({
                _id: u._id,
                phoneNumber: u.phoneNumber || "",
                name: u.verifyId ? u.verifyId.idName : u.name || "",
                username: u.name || "",
                staff: u.isStaff || false,
                counter: u.isCounter || false,
                serviceActive: u.serviceActive ?? false,
                merchantId: u.merchantId || null
            });
        } else {
            setForm({
                _id: "",
                phoneNumber: "",
                name: "",
                username: "",
                staff: false,
                counter: false,
                serviceActive: false,
                merchantId: null
            });
        }
    }, [setDialog.data]);

    const save = async () => {
        try {
            setIsSaving(true);
            const { data, error } = await updateUserServiceRole({
                _id: form._id,
                staff: form.staff,
                counter: form.counter,
                serviceActive: form.serviceActive,
                merchantId: form.merchantId
            });
            if (error) throw error;
            if (data) {
                toast.success("Service role updated");
            }
            handleClose(true);
        } catch (e) {
            console.error(e);
            toast.error("Failed to update role");
        } finally {
            setIsSaving(false);
        }
    };

    const deleteRole = async () => {
        try {
            setIsDeleting(true);

            const { data, error } = await updateUserServiceRole({
                _id: form._id,
                placeId
            });
            if (error) throw error;
            if (data) {
                toast.success("Service role removed");
            }
            handleClose(true);
        } catch (e) {
            console.error(e);
            toast.error("Failed to remove role");
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <ConfirmDialogComponent
                open={confirmOpen}
                onCancel={() => setConfirmOpen(false)}
                onConfirm={async () => {
                    await deleteRole();
                    setConfirmOpen(false);
                }}
                loading={isDeleting}
                title="Remove Service Role?"
                content="This will remove the service role from the user."
                confirmLabel="Remove"
                cancelLabel="Cancel"
                loadingConfirmLabel="Removing..."
            />

            <Dialog open={setDialog.open} onClose={handleClose}>
                <Box sx={{ padding: 2, width: "auto", maxWidth: 420 }}>
                    <Typography variant="h6" gutterBottom>
                        Update Service Role
                    </Typography>

                    <Box component="form" noValidate autoComplete="off">
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TCInput
                                    fullWidth
                                    label="Phone Number"
                                    value={form.phoneNumber}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TCInput fullWidth label="Name" value={form.name} disabled />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TCInput fullWidth label="Username" value={form.username} disabled />
                            </Grid>

                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item>
                                        <TCLabel style={{ color: 'rgb(133, 133, 133)' }}>Service Role*</TCLabel>
                                    </Grid>
                                    <Grid item>
                                        <TCLabel style={{ color: 'red' }}>{joi.serviceRole}</TCLabel>
                                    </Grid>
                                </Grid>

                                <FormControlLabel sx={{ padding: -1 }} disabled={false} control={<Checkbox checked={form.counter}
                                    onChange={() => {
                                        setForm({ ...form, counter: !form.counter })
                                    }} />} label={<TCLabel>Counter</TCLabel>}
                                />
                                <FormControlLabel sx={{ padding: -1 }} disabled={false} control={<Checkbox checked={form.staff}
                                    onChange={() => {
                                        setForm({ ...form, staff: !form.staff })
                                    }} />} label={<TCLabel>Staff</TCLabel>}
                                />
                            </Grid>

                            <Grid container>
                                <Grid item xs={12} sx={{ paddingLeft: 2 }}>
                                    <TCSelect
                                        label={'Status'}
                                        inDialog
                                        value={form.serviceActive ? {
                                            label: "Active",
                                            value: "active"
                                        } : {
                                            label: "Inactive",
                                            value: "inactive"
                                        }}
                                        options={[
                                            {
                                                label: "Active",
                                                value: "active"
                                            }, {
                                                label: "Inactive",
                                                value: "inactive"
                                            }]}
                                        onChange={(e) => {
                                            setForm({ ...form, serviceActive: e.value === "active" })
                                        }}
                                    />
                                </Grid>
                            </Grid>

                        </Grid>

                        <BoxButtonComponent justify={form?._id ? "space-between" : "flex-end"} direction="row">
                            {form?._id && (
                                <ButtonComponent
                                    onClick={() => setConfirmOpen(true)}
                                    loading={isDeleting}
                                    label="Remove Role"
                                    loadingLabel="Removing..."
                                    buttonColor="error"
                                    spinnerColor="error.main"
                                    textColor="#fff"
                                />
                            )}

                            <Box display="flex" gap={1}>
                                <Button variant="outlined" color="secondary" onClick={() => handleClose(false)}>
                                    Cancel
                                </Button>
                                <ButtonComponent
                                    disabled={!form._id || !(form.staff || form.counter)}
                                    onClick={save}
                                    loading={isSaving}
                                    label="Save"
                                    loadingLabel="Saving..."
                                />
                            </Box>
                        </BoxButtonComponent>
                    </Box>
                </Box>
            </Dialog>
        </>
    );
}
